import { type LocalizedText, localize } from "@ando/ui";
/**
 * Vendor brand SVGs — ported from v1 EmailHubPage / DriveHubPage / EnterpriseHubPage.
 *
 * Lucide icons (the generic `<Mail/>`) make every capability row look
 * identical when a single capability covers multiple vendors. Ported the
 * brand marks so users can tell Gmail from Outlook from Salesforce at a
 * glance — v1 UX recognising brands is part of why the v1 page "felt
 * right" while the v2 page felt broken.
 *
 * ── May 24, 2026 — DRY pass ────────────────────────────────────────────
 *
 *  1. 4 monogram-style brand marks (Aruba, InfoCert, SAP, Teams) shared
 *     the SAME ``<rect/> + <text/>`` SVG skeleton. Extracted as
 *     ``<MonogramIcon>``: each brand is now a 1-line wrapper.
 *
 *  2. The previous ``VENDOR_ICONS`` and ``VENDOR_COLORS`` parallel maps
 *     were a drift hazard — adding Notion's icon without its color (or
 *     vice-versa) compiled clean and broke at runtime. Unified into a
 *     single ``VENDORS`` map keyed by slug with both fields in one row.
 *
 * ── June 4, 2026 — real brand marks via ``react-icons`` ────────────────
 * The hand-rolled SVGs for cloud providers (OneDrive nuvola, SharePoint
 * 3-circle, Salesforce blob) looked broken next to V1 — user-reported
 * "loghi spezzati" on the integrations hub. V1 + landing page both pull
 * the official trademark icons via ``react-icons`` (5.6.0); we now do
 * the same for every multi-vendor consumer brand (Google Drive, Gmail,
 * OneDrive, SharePoint, Outlook, Teams, Notion, Slack, Salesforce,
 * HubSpot, Dropbox). Italian-niche brands (Aruba, InfoCert, FiC) keep
 * their inline-SVG since ``react-icons`` doesn't ship trademarks for
 * them, and database tiles keep the ``DatabaseTileIcon`` monogram.
 *
 * Tree-shake guarantee: each ``import {Foo} from "react-icons/<pack>"``
 * pulls only Foo + the pack's tiny shared wrapper. Bundle delta measured
 * after the swap: < 4 KB gzipped.
 */
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  Archive,
  BadgeCheck,
  DraftingCompass,
  FileCheck,
  FolderTree,
  Landmark,
  Receipt,
} from "lucide-react";
import { FaDropbox, FaHubspot, FaMicrosoft, FaSalesforce } from "react-icons/fa";
import { SiGmail, SiGooglecalendar, SiGooglechat, SiTrello, SiWhatsapp } from "react-icons/si";

interface IconProps {
  readonly className?: string;
}

// ``react-icons`` ships its 5+ ``IconBase`` props (className, color,
// title, role, …) but most call-sites need the same 4: className, the
// brand colour, the trademark title, and the standard hidden/focusable
// pair. Pass-through this helper so every brand row reads the same
// — DRY against silent drift between brands (reviewer #4 caught the
// missing ``<title>`` on react-icons brands vs the inline-SVG siblings).
function brandIconProps(label: string, color: string, className?: string) {
  return {
    className,
    color,
    title: label,
    "aria-hidden": true,
    focusable: false,
  } as const;
}

interface MonogramProps extends IconProps {
  readonly label: string;
  readonly bg: string;
  readonly fontSize?: number;
  readonly rx?: number;
}

// ── Brand color palette — single source of truth ────────────────────────
// Each hex value lives ONCE. Inline-SVG ``fill="…"`` strings inside the
// brand SVGs below are intentionally NOT templated through this map
// (each brand SVG is a one-shot definition copied from a brand kit);
// dynamic-color icons (monogram + database-tile factories) read from
// here so the ``VENDORS`` map at the bottom can't drift.
const BRAND_HEX = {
  gmail: "#EA4335",
  outlook: "#0078D4",
  google_drive: "#4285F4",
  onedrive: "#0364B8",
  sharepoint: "#03787C",
  dropbox: "#0061FF",
  salesforce: "#00A1E0",
  hubspot: "#FF7A59",
  trello: "#0079BF",
  sap: "#0FAAFF",
  slack: "#611F69",
  teams: "#4B53BC",
  aruba: "#00A0E1",
  infocert: "#005CA9",
  fatture_in_cloud: "#0066B3",
  notion: "#000000",
  postgresql: "#336791",
  mysql: "#00758F",
  sql_server: "#A91D22",
  oracle: "#F80000",
  sqlite: "#7C8A9A",
  generic_odbc: "#5A6776",
  zucchetti: "#E4002B",
  teamsystem: "#1E3A5F",
  spreadsheet: "#16A34A",
  // ── June 5, 2026 — capability-tile brand colors ────────────────────
  // For capabilities that route through a tenant-resolved backend driver
  // (white-label) and so don't have ONE vendor brand to surface. Each
  // hue picks an Italian-public-service-adjacent palette so the tile is
  // visually distinct from the consumer brands above.
  whatsapp: "#25D366", // WhatsApp official green
  google_calendar: "#4285F4", // Google blue
  google_chat: "#00AC47", // Google Chat green
  cad_library: "#475569", // industrial slate
  cassetto_fiscale: "#DC2626", // AdE fiscal red
  legal_storage: "#B45309", // archive amber
  sdi_invoicing: "#059669", // SDI emerald
  vat_verifier: "#2563EB", // verify blue
  open_banking: "#1E40AF", // bank navy
  filesystem: "#6B7280", // folder gray
} as const satisfies Record<string, string>;

// ── Italian human label per vendor — single source of truth ─────────────
// Used by ``ConnectedAccountRow`` (replaces its private VENDOR_LABELS map)
// and any future surface that needs to translate a BE slug to a label.
// Missing slugs fall back to a Title-Case humanizer in ``vendorLabel``.
const VENDOR_LABEL: Readonly<Record<string, LocalizedText>> = {
  gmail: "Gmail",
  outlook: "Outlook",
  google_drive: "Google Drive",
  onedrive: "OneDrive",
  sharepoint: "SharePoint",
  dropbox: "Dropbox",
  salesforce: "Salesforce",
  hubspot: "HubSpot",
  trello: "Trello",
  sap: "SAP",
  sap_odata: "SAP",
  slack: "Slack",
  teams: "Microsoft Teams",
  aruba: "Aruba",
  aruba_mail: "Aruba Mail",
  aruba_pec: "Aruba PEC",
  infocert: "InfoCert",
  infocert_pec: "InfoCert PEC",
  fatture_in_cloud: "Fatture in Cloud",
  notion: "Notion",
  postgresql: "PostgreSQL",
  mysql: "MySQL",
  sql_server: "SQL Server",
  oracle: "Oracle",
  sqlite: "SQLite",
  generic_odbc: "ODBC",
  zucchetti: "Zucchetti",
  teamsystem: "TeamSystem",
  upload: msg`Foglio di Calcolo (locale)`,
  spreadsheet: msg`Foglio di Calcolo`,
  // June 5, 2026 — labels for vendors/capabilities added with their icons.
  whatsapp: "WhatsApp Business",
  google_calendar: "Google Calendar",
  outlook_calendar: "Outlook Calendar",
  google_chat: "Google Chat",
  sap_b1: "SAP Business One",
  cad_library: msg`Libreria CAD`,
  cassetto_fiscale: msg`Cassetto Fiscale`,
  legal_storage: msg`Conservazione a norma`,
  sdi_invoicing: msg`SDI Fatturazione`,
  vat_verifier: msg`Verifica P.IVA`,
  open_banking: msg`Open Banking`,
  filesystem: msg`Filesystem locale`,
};

/**
 * Square monogram badge — text initial(s) on a brand-colored rounded
 * background. Used for brands without a recognisable mark we can paint
 * in 24×24 (Aruba "A", InfoCert "iC", SAP "SAP", Teams "T").
 */
const MonogramIcon = ({
  label,
  bg,
  fontSize = 11,
  rx = 4,
  className,
}: MonogramProps): React.JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>{label}</title>
    <rect width="24" height="24" rx={rx} fill={bg} />
    <text
      x="12"
      y="16"
      textAnchor="middle"
      fontSize={fontSize}
      fontFamily="system-ui,-apple-system,sans-serif"
      fontWeight="700"
      fill="white"
    >
      {label}
    </text>
  </svg>
);

/**
 * Tinted ``Database`` lucide-style mark — used for the six SQL engines we
 * ship in the ``database`` capability (PostgreSQL, MySQL, SQL Server,
 * Oracle, SQLite, ODBC). None of these have a 24px-readable brand mark
 * (the elephant / dolphin / etc are too detailed at this size), so we
 * paint a recognisable database cylinder on a brand-coloured tile —
 * matches v1 ``EnterpriseHubPage`` which uses the same lucide ``Database``
 * with a per-engine ``color`` style.
 */
const DatabaseTileIcon = ({
  bg,
  className,
}: IconProps & { readonly bg: string }): React.JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>Database</title>
    <rect width="24" height="24" rx="4" fill={bg} />
    <ellipse cx="12" cy="7" rx="5" ry="2" fill="white" />
    <path
      d="M7 7v10c0 1.1 2.24 2 5 2s5-.9 5-2V7"
      stroke="white"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M7 12c0 1.1 2.24 2 5 2s5-.9 5-2"
      stroke="white"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Capability tile — lucide glyph centered on a brand-tinted rounded
 * square. Used for white-label CAPABILITIES (cassetto_fiscale, SDI,
 * open_banking, …) that route to a tenant-resolved backend driver and
 * therefore don't have ONE vendor brand to surface. Mirrors the visual
 * weight of the database engines + monogram tiles so the AddAccountMenu
 * grid stays uniform.
 *
 * Implemented as a div wrapper instead of SVG ``foreignObject`` (which
 * fails on SSR + has spotty browser support) so the lucide glyph renders
 * cleanly and inherits Tailwind sizing classes from the consumer.
 */
type LucideGlyph = typeof Archive;

interface CapabilityTileProps extends IconProps {
  readonly Glyph: LucideGlyph;
  readonly bg: string;
  readonly label: LocalizedText;
}

const CapabilityTileIcon = ({
  Glyph,
  bg,
  label,
  className,
}: CapabilityTileProps): React.JSX.Element => {
  // Resolved at render-time so the user's active locale wins even though
  // the call sites pass the macro output (``msg`…```) raw — DRY with
  // ``vendorLabel()`` which does the same lookup for the slug-keyed map.
  const resolvedLabel = localize(label, i18nSingleton);
  return (
    <div
      role="img"
      aria-label={resolvedLabel}
      title={resolvedLabel}
      className={`inline-flex items-center justify-center rounded ${className ?? ""}`.trim()}
      style={{ backgroundColor: bg }}
    >
      <Glyph className="h-[58%] w-[58%]" color="white" strokeWidth={2} aria-hidden="true" />
    </div>
  );
};

// ── Brand SVGs (alphabetical) ───────────────────────────────────────────

export const ArubaIcon = (p: IconProps): React.JSX.Element => (
  <MonogramIcon label="A" bg={BRAND_HEX.aruba} {...p} />
);

// ── Database engine marks — single ``DatabaseTileIcon`` factory per
//    engine. Brand colors mirror v1 ``EnterpriseHubPage`` (PostgreSQL
//    #336791) where available; SQLite + ODBC fall back to ink-tones
//    because no brand color exists. ──────────────────────────────────────

export const PostgreSqlIcon = (p: IconProps): React.JSX.Element => (
  <DatabaseTileIcon bg={BRAND_HEX.postgresql} {...p} />
);

export const MySqlIcon = (p: IconProps): React.JSX.Element => (
  <DatabaseTileIcon bg={BRAND_HEX.mysql} {...p} />
);

export const SqlServerIcon = (p: IconProps): React.JSX.Element => (
  <DatabaseTileIcon bg={BRAND_HEX.sql_server} {...p} />
);

export const OracleIcon = (p: IconProps): React.JSX.Element => (
  <DatabaseTileIcon bg={BRAND_HEX.oracle} {...p} />
);

export const SqliteIcon = (p: IconProps): React.JSX.Element => (
  <DatabaseTileIcon bg={BRAND_HEX.sqlite} {...p} />
);

export const OdbcIcon = (p: IconProps): React.JSX.Element => (
  <DatabaseTileIcon bg={BRAND_HEX.generic_odbc} {...p} />
);

export const DropboxIcon = ({ className }: IconProps): React.JSX.Element => (
  <FaDropbox {...brandIconProps("Dropbox", BRAND_HEX.dropbox, className)} />
);

export const FattureInCloudIcon = ({ className }: IconProps): React.JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>Fatture in Cloud</title>
    <rect width="24" height="24" rx="4" fill="#0066B3" />
    <path fill="white" d="M6 6h12v3H9v2.5h7v3H9V18H6V6Z" />
  </svg>
);

export const GmailIcon = ({ className }: IconProps): React.JSX.Element => (
  <SiGmail {...brandIconProps("Gmail", BRAND_HEX.gmail, className)} />
);

export const GoogleDriveIcon = ({ className }: IconProps): React.JSX.Element => (
  // 3-color triangle painted inline — the canonical Drive trademark.
  // react-icons' ``FaGoogleDrive`` is monocolor (single path); keeping
  // the inline SVG preserves the brand-recognisable yellow/green/blue.
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>Google Drive</title>
    <path fill="#4285F4" d="M4.4 22 0 14.4 8 0h7.2L7.2 14.4Z" />
    <path fill="#34A853" d="m12 22 4-7.6 8-1.4-4 9Z" />
    <path fill="#FBBC04" d="M8 14.4 12 22h12L20 14.4Z" />
  </svg>
);

export const HubSpotIcon = ({ className }: IconProps): React.JSX.Element => (
  <FaHubspot {...brandIconProps("HubSpot", BRAND_HEX.hubspot, className)} />
);

export const TrelloIcon = ({ className }: IconProps): React.JSX.Element => (
  // Simple Icons trademark mark — the single vendor behind the white-label
  // ``project_management`` capability (mirrors SalesforceIcon/HubSpotIcon
  // under ``crm``). Canonical Trello blue ``BRAND_HEX.trello``.
  <SiTrello {...brandIconProps("Trello", BRAND_HEX.trello, className)} />
);

export const InfocertIcon = (p: IconProps): React.JSX.Element => (
  <MonogramIcon label="iC" bg={BRAND_HEX.infocert} fontSize={10} {...p} />
);

export const NotionIcon = ({ className }: IconProps): React.JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>Notion</title>
    <path
      fill="currentColor"
      d="M4.5 3.7c.6.5 1 .5 2 .4L19.3 3c.3 0 .1-.3-.1-.4l-2-1.4c-.4-.3-.9-.6-1.9-.5L3 1.7c-.4 0-.5.2-.4.4l1.9 1.6ZM5.2 6v13c0 .7.3 1 1.1.9l13.9-.8c.8-.1 1-.5 1-1.2V4.7c0-.7-.3-1-.9-1L6.5 4.5c-.7 0-1 .4-1 1.1l-.3.4Zm13.4.7c0 .4-.2.5-.6.6l-.7.1V18c-.6.3-1 .4-1.5.4-.7 0-.9-.2-1.4-.8l-4.4-7v6.8l1.4.3s0 .8-1.1.8l-3.1.2c-.1-.2 0-.7.4-.8l.8-.2V8l-1.2-.1c-.1-.4.2-.9.7-.9L11 6.8l4.6 7V7.6L14.4 7.4c-.1-.5.2-.9.6-.9l3.6.2Z"
    />
  </svg>
);

// Microsoft Fluent product assets (48px SVG), stored locally so Console and
// Workspace render the same full-colour marks without a runtime CDN request.
// Source: https://res-2-sdf.cdn.office.net/files/fabric-cdn-prod_20240411.001/assets/brand-icons/product/svg/
export const OutlookIcon = ({ className }: IconProps): React.JSX.Element => (
  <img src="/vendor-icons/outlook.svg" className={className} alt="" title="Outlook" aria-hidden="true" />
);

export const OneDriveIcon = ({ className }: IconProps): React.JSX.Element => (
  <img src="/vendor-icons/onedrive.svg" className={className} alt="" title="OneDrive" aria-hidden="true" />
);

export const SalesforceIcon = ({ className }: IconProps): React.JSX.Element => (
  // Canonical "cloud" Salesforce wordmark — same as V1 ProviderCard +
  // landing page. The previous freehand cloud blob looked generic.
  <FaSalesforce {...brandIconProps("Salesforce", BRAND_HEX.salesforce, className)} />
);

export const SapIcon = (p: IconProps): React.JSX.Element => (
  <MonogramIcon label="SAP" bg={BRAND_HEX.sap} fontSize={9} rx={3} {...p} />
);

export const SharePointIcon = ({ className }: IconProps): React.JSX.Element => (
  // ``react-icons`` doesn't ship a SharePoint-specific trademark; V1
  // ProviderCard.tsx uses ``FaMicrosoft`` for SharePoint rows for the
  // same reason. The previous "3 circles" hand-roll looked broken next
  // to the polished react-icons brands around it (June 4 2026 report).
  // Microsoft logo + SharePoint-teal colour token keeps the row
  // recognisable as a Microsoft 365 service.
  <FaMicrosoft {...brandIconProps("SharePoint", BRAND_HEX.sharepoint, className)} />
);

export const SlackIcon = ({ className }: IconProps): React.JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>Slack</title>
    <path fill="#E01E5A" d="M5 15a2 2 0 1 1-2-2h2v2Zm1 0a2 2 0 1 1 4 0v5a2 2 0 1 1-4 0v-5Z" />
    <path fill="#36C5F0" d="M9 5a2 2 0 1 1 2-2v2H9Zm0 1a2 2 0 1 1 0 4H4a2 2 0 1 1 0-4h5Z" />
    <path fill="#2EB67D" d="M19 9a2 2 0 1 1 2 2h-2V9Zm-1 0a2 2 0 1 1-4 0V4a2 2 0 1 1 4 0v5Z" />
    <path fill="#ECB22E" d="M15 19a2 2 0 1 1-2 2v-2h2Zm0-1a2 2 0 1 1 0-4h5a2 2 0 1 1 0 4h-5Z" />
  </svg>
);

export const TeamsIcon = ({ className }: IconProps): React.JSX.Element => (
  <img src="/vendor-icons/teams.svg" className={className} alt="" title="Microsoft Teams" aria-hidden="true" />
);

// ── ERP (PR-INT-3) — partner-gated brands. No public brand-mark SVG that
//    survives 24px, so we render text monograms in the official Pantone
//    accents (Zucchetti red, TeamSystem navy). Matches the V1
//    ``/icons/zucchetti.png`` + ``/icons/teamsystem.png`` colour palette
//    without pulling raster assets into the V2 bundle.
export const ZucchettiIcon = (p: IconProps): React.JSX.Element => (
  <MonogramIcon label="Z" bg={BRAND_HEX.zucchetti} fontSize={13} {...p} />
);

export const TeamSystemIcon = (p: IconProps): React.JSX.Element => (
  <MonogramIcon label="TS" bg={BRAND_HEX.teamsystem} fontSize={9} {...p} />
);

/**
 * SpreadsheetUploadIcon — neutral green tile used for the ``upload``
 * source-mode of the ``spreadsheet`` capability (PR-INT-2). The Drive
 * and OneDrive source-modes reuse the existing brand marks; only the
 * local-upload mode needs a dedicated tile so the picker dropdown shows
 * three visually distinct options at a glance.
 *
 * Green (#16A34A) mirrors V1 ``EnterpriseHubPage.tsx`` line 340 ("Foglio
 * di Calcolo" tile color). The mark is a stylised spreadsheet grid drawn
 * on a rounded brand-coloured tile.
 */
export const SpreadsheetUploadIcon = ({ className }: IconProps): React.JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <title>{i18nSingleton._(msg`Foglio di Calcolo (upload locale)`)}</title>
    <rect width="24" height="24" rx="4" fill={BRAND_HEX.spreadsheet} />
    <rect x="5" y="6" width="14" height="12" rx="1" fill="white" />
    <line x1="5" y1="10" x2="19" y2="10" stroke={BRAND_HEX.spreadsheet} strokeWidth="1" />
    <line x1="5" y1="14" x2="19" y2="14" stroke={BRAND_HEX.spreadsheet} strokeWidth="1" />
    <line x1="12" y1="6" x2="12" y2="18" stroke={BRAND_HEX.spreadsheet} strokeWidth="1" />
  </svg>
);

// ── June 5, 2026 — Real brand marks via react-icons ────────────────────

export const WhatsAppIcon = ({ className }: IconProps): React.JSX.Element => (
  <SiWhatsapp {...brandIconProps("WhatsApp Business", BRAND_HEX.whatsapp, className)} />
);

export const GoogleCalendarIcon = ({ className }: IconProps): React.JSX.Element => (
  <SiGooglecalendar {...brandIconProps("Google Calendar", BRAND_HEX.google_calendar, className)} />
);

export const GoogleChatIcon = ({ className }: IconProps): React.JSX.Element => (
  <SiGooglechat {...brandIconProps("Google Chat", BRAND_HEX.google_chat, className)} />
);

// Outlook Calendar reuses the canonical Outlook envelope mark — same
// Microsoft brand, calendar capability scope.
export const OutlookCalendarIcon = ({ className }: IconProps): React.JSX.Element => (
  <img src="/vendor-icons/outlook.svg" className={className} alt="" title="Outlook Calendar" aria-hidden="true" />
);

// ── June 5, 2026 — Capability tiles (white-label, no single vendor) ───

export const CadLibraryIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={DraftingCompass}
    bg={BRAND_HEX.cad_library}
    label={msg`Libreria CAD`}
    {...p}
  />
);

export const CassettoFiscaleIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={FileCheck}
    bg={BRAND_HEX.cassetto_fiscale}
    label={msg`Cassetto Fiscale`}
    {...p}
  />
);

export const LegalStorageIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={Archive}
    bg={BRAND_HEX.legal_storage}
    label={msg`Conservazione a norma`}
    {...p}
  />
);

export const SdiInvoicingIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={Receipt}
    bg={BRAND_HEX.sdi_invoicing}
    label={msg`SDI Fatturazione Elettronica`}
    {...p}
  />
);

export const VatVerifierIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={BadgeCheck}
    bg={BRAND_HEX.vat_verifier}
    label={msg`Verifica P.IVA`}
    {...p}
  />
);

export const OpenBankingIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={Landmark}
    bg={BRAND_HEX.open_banking}
    label={msg`Open Banking`}
    {...p}
  />
);

export const FilesystemIcon = (p: IconProps): React.JSX.Element => (
  <CapabilityTileIcon
    Glyph={FolderTree}
    bg={BRAND_HEX.filesystem}
    label={msg`Filesystem locale`}
    {...p}
  />
);

// ── Single source of truth: icon + brand color per slug ─────────────────

interface VendorEntry {
  readonly Icon: (props: IconProps) => React.JSX.Element;
  readonly color: string;
}

const VENDORS: Readonly<Record<string, VendorEntry>> = {
  // Email
  gmail: { Icon: GmailIcon, color: BRAND_HEX.gmail },
  outlook: { Icon: OutlookIcon, color: BRAND_HEX.outlook },
  // Drive
  google_drive: { Icon: GoogleDriveIcon, color: BRAND_HEX.google_drive },
  onedrive: { Icon: OneDriveIcon, color: BRAND_HEX.onedrive },
  sharepoint: { Icon: SharePointIcon, color: BRAND_HEX.sharepoint },
  dropbox: { Icon: DropboxIcon, color: BRAND_HEX.dropbox },
  // CRM / ERP
  salesforce: { Icon: SalesforceIcon, color: BRAND_HEX.salesforce },
  hubspot: { Icon: HubSpotIcon, color: BRAND_HEX.hubspot },
  // Project management (white-label) — single vendor Trello.
  trello: { Icon: TrelloIcon, color: BRAND_HEX.trello },
  sap: { Icon: SapIcon, color: BRAND_HEX.sap },
  // ERP (PR-INT-3) — ``sap_odata`` aliases the existing SAP mark; Zucchetti
  // + TeamSystem ship as partner-gated tier-4 entries today (greyed out
  // in the AddAccountMenu via ``VendorOption.disabled``).
  sap_odata: { Icon: SapIcon, color: BRAND_HEX.sap },
  // SAP Business One reuses the same SAP brand mark; the spec exposes
  // it as a separate vendor because its onboarding step is credentials-
  // based (Service Layer endpoint + Company DB) whereas sap_odata is
  // OAuth-based.
  sap_b1: { Icon: SapIcon, color: BRAND_HEX.sap },
  zucchetti: { Icon: ZucchettiIcon, color: BRAND_HEX.zucchetti },
  teamsystem: { Icon: TeamSystemIcon, color: BRAND_HEX.teamsystem },
  // Messaging
  slack: { Icon: SlackIcon, color: BRAND_HEX.slack },
  teams: { Icon: TeamsIcon, color: BRAND_HEX.teams },
  whatsapp: { Icon: WhatsAppIcon, color: BRAND_HEX.whatsapp },
  google_chat: { Icon: GoogleChatIcon, color: BRAND_HEX.google_chat },
  // Calendar
  google_calendar: { Icon: GoogleCalendarIcon, color: BRAND_HEX.google_calendar },
  outlook_calendar: { Icon: OutlookCalendarIcon, color: BRAND_HEX.outlook },
  // Italian
  aruba_pec: { Icon: ArubaIcon, color: BRAND_HEX.aruba },
  aruba_mail: { Icon: ArubaIcon, color: BRAND_HEX.aruba },
  aruba: { Icon: ArubaIcon, color: BRAND_HEX.aruba },
  infocert_pec: { Icon: InfocertIcon, color: BRAND_HEX.infocert },
  infocert: { Icon: InfocertIcon, color: BRAND_HEX.infocert },
  fatture_in_cloud: { Icon: FattureInCloudIcon, color: BRAND_HEX.fatture_in_cloud },
  // Productivity
  notion: { Icon: NotionIcon, color: BRAND_HEX.notion },
  // Direct DB engines — PR-INT-1 (V1 parity for the "Direct DB" tile).
  // Brand-coloured database cylinders; no detailed brand marks at 24px.
  postgresql: { Icon: PostgreSqlIcon, color: BRAND_HEX.postgresql },
  mysql: { Icon: MySqlIcon, color: BRAND_HEX.mysql },
  sql_server: { Icon: SqlServerIcon, color: BRAND_HEX.sql_server },
  oracle: { Icon: OracleIcon, color: BRAND_HEX.oracle },
  sqlite: { Icon: SqliteIcon, color: BRAND_HEX.sqlite },
  generic_odbc: { Icon: OdbcIcon, color: BRAND_HEX.generic_odbc },
  // Spreadsheet source-modes (PR-INT-2). ``upload`` ships its own neutral
  // green tile; ``google_drive`` / ``onedrive`` reuse the Drive marks
  // already registered above. The capability-slug fallback
  // (``spreadsheet`` key) is used by ConnectAccountDialog when the user
  // hasn't picked a source-mode yet (e.g. the empty-state CTA).
  upload: { Icon: SpreadsheetUploadIcon, color: BRAND_HEX.spreadsheet },
  spreadsheet: { Icon: SpreadsheetUploadIcon, color: BRAND_HEX.spreadsheet },
  // ── June 5, 2026 — Italian fiscal/banking + filesystem capability tiles ─
  // Each routes server-side through ``ConnectorFactory._resolve(tenant_id,
  // capability)`` — no single vendor brand to surface, so we paint a
  // lucide glyph on a brand-tinted tile (matches the database engines).
  cad_library: { Icon: CadLibraryIcon, color: BRAND_HEX.cad_library },
  cassetto_fiscale: { Icon: CassettoFiscaleIcon, color: BRAND_HEX.cassetto_fiscale },
  legal_storage: { Icon: LegalStorageIcon, color: BRAND_HEX.legal_storage },
  sdi_invoicing: { Icon: SdiInvoicingIcon, color: BRAND_HEX.sdi_invoicing },
  vat_verifier: { Icon: VatVerifierIcon, color: BRAND_HEX.vat_verifier },
  open_banking: { Icon: OpenBankingIcon, color: BRAND_HEX.open_banking },
  filesystem: { Icon: FilesystemIcon, color: BRAND_HEX.filesystem },
};

export function vendorIcon(
  provider: string | undefined,
  className?: string,
): React.JSX.Element | null {
  if (!provider) return null;
  const row = VENDORS[provider.toLowerCase()];
  return row ? <row.Icon className={className} /> : null;
}

export function vendorBrandColor(provider: string | undefined): string | null {
  if (!provider) return null;
  return VENDORS[provider.toLowerCase()]?.color ?? null;
}

/**
 * Human label for a vendor slug ("google_drive" → "Google Drive",
 * "fatture_in_cloud" → "Fatture in Cloud"). Falls back to a Title-Case
 * humanizer that splits on underscore so newly-added BE slugs render
 * sensibly even before they get an explicit entry here.
 */
export function vendorLabel(slug: string | undefined): string {
  if (!slug) return "";
  const key = slug.toLowerCase();
  const known = VENDOR_LABEL[key];
  // Resolve LocalizedText (string passes through, MessageDescriptor goes
  // through ``i18nSingleton._()`` → reads the active catalog at call time
  // so the label reflects the user's locale even when this VENDOR_LABEL
  // map was populated at module load.
  if (known !== undefined) return localize(known, i18nSingleton);
  return key
    .split("_")
    .map((p) => (p.length > 0 ? p[0]!.toUpperCase() + p.slice(1) : p))
    .join(" ");
}
